/**
 * Pure permission-resolution helpers.
 *
 * Single source of truth for client-side permission checks, shared by
 * AuthContext and the usePermissions hook so the two never diverge.
 * The backend remains the authoritative enforcer; these only drive UI
 * visibility.
 *
 * Multi-tenancy (v0.13.0) changed what these read. Authority used to come from a global
 * `user.system_role`; it now comes from two independent tiers:
 *
 *   - `platform_role`  — Akhile's back office (audit, email config, auth policy, tenant
 *                        administration). Never held by a client, whatever their standing in
 *                        their own tenant.
 *   - `tenant_role`    — authority inside the currently active tenant only.
 *
 * Keeping them apart is the point: a client's Owner is the most senior person in their
 * tenant and still must not reach Akhile's back office, because that holds every tenant's
 * audit trail and a shared email credential.
 *
 * Hierarchy inside a tenant (highest wins):
 *   Owner / Admin / Portfolio Manager > Programme role > Project role > default.
 */

import type { TenantRole, User } from '../types/auth';

/**
 * Every role assignable within a tenant, most senior first. The single list the role pickers
 * read, so adding a role here reaches every dropdown at once. Owner is deliberately included:
 * a workspace may want more than one.
 */
export const TENANT_ROLES: TenantRole[] = [
  'Owner',
  'Admin',
  'Portfolio Manager',
  'Member',
  'Viewer',
];

/** Roles that may administer a tenant's own users, roles and seats. */
const TENANT_ADMIN_ROLES: TenantRole[] = ['Owner', 'Admin'];

/** Roles with portfolio-wide authority inside their tenant. */
const TENANT_PORTFOLIO_ROLES: TenantRole[] = ['Owner', 'Admin', 'Portfolio Manager'];

/**
 * Akhile staff. Gates the platform back office only — it grants nothing inside a client
 * tenant, where a membership is still required.
 */
export const isPlatformAdmin = (user: User | null): boolean =>
  !!user && user.platform_role === 'Platform Admin';

/** May invite people, change their roles and manage seats within the active tenant. */
export const isTenantAdmin = (user: User | null): boolean =>
  !!user && !!user.tenant_role && TENANT_ADMIN_ROLES.includes(user.tenant_role);

/**
 * Portfolio-wide authority within the active tenant (Owner, Admin or Portfolio Manager).
 *
 * Note this is scoped: being a Portfolio Manager of tenant A says nothing about tenant B.
 * The name is kept from the pre-tenancy model so existing call sites keep reading naturally.
 */
export const isPortfolioManager = (user: User | null): boolean =>
  !!user && !!user.tenant_role && TENANT_PORTFOLIO_ROLES.includes(user.tenant_role);

/** Read-only within the active tenant. */
export const isTenantViewer = (user: User | null): boolean =>
  !!user && user.tenant_role === 'Viewer';

/** Who may create/edit/delete Programmes within the active tenant. */
export const canManageProgrammes = (user: User | null): boolean => isPortfolioManager(user);

/** Whether to show a tenant switcher at all — only useful with more than one tenant. */
export const hasMultipleTenants = (user: User | null): boolean =>
  (user?.memberships?.filter((m) => m.status === 'active').length ?? 0) > 1;

/**
 * True when the session has no tenant to act in. Every domain endpoint returns 400 in this
 * state, so the UI should say so plainly rather than rendering empty lists that look like
 * "you have no projects".
 *
 * Deliberately keyed on an explicit `null`, not on falsiness. This predicate gates the whole
 * application (see ProtectedRoute), so a missing field must not read as "no workspace" — a
 * frontend deployed ahead of its backend, or any payload that predates tenancy, would then
 * lock every client user out of everything with no way back. The backend sends
 * `active_tenant_id: null` for a genuinely tenantless user (UserRead has no exclude_none), so
 * `undefined` can only mean "this response never carried the field" and is failed open.
 *
 * A listed active membership contradicts the null and is trusted over it: an inconsistent
 * payload should land the user in front of the API's own answer, not this dead end.
 */
export const hasNoActiveTenant = (user: User | null): boolean => {
  if (!user) return false;
  if (user.active_tenant_id !== null) return false;
  return !user.memberships?.some((m) => m.status === 'active');
};

export const canEditProject = (
  user: User | null,
  projectId: string,
  programmeId?: string
): boolean => {
  if (!user) return false;

  // A session with no tenant cannot edit anything — the API will refuse regardless. Without
  // this, a platform admin between engagements (exempted from the tenantless screen so the
  // back office stays reachable) is offered edit controls off stale `project_roles`.
  if (hasNoActiveTenant(user)) return false;

  // Portfolio-wide authority within the active tenant
  if (isPortfolioManager(user)) return true;

  // Project-level Editor
  const projectRoles = user.project_roles || [];
  if (projectRoles.some((r) => r.project_id === projectId && r.role === 'Editor')) {
    return true;
  }

  // Programme-level Editor (when the project's programme is known)
  if (programmeId) {
    const programmeRoles = user.programme_roles || [];
    if (programmeRoles.some((r) => r.programme_id === programmeId && r.role === 'Editor')) {
      return true;
    }
  }

  return false;
};

export const canViewProject = (
  user: User | null,
  projectId: string,
  programmeId?: string
): boolean => {
  if (!user) return false;

  // A session with no tenant cannot view anything — the API will refuse regardless.
  if (hasNoActiveTenant(user)) return false;

  // Portfolio-wide authority within the active tenant
  if (isPortfolioManager(user)) return true;

  const projectRoles = user.project_roles || [];
  if (projectRoles.some((r) => r.project_id === projectId && ['Editor', 'Viewer'].includes(r.role))) {
    return true;
  }

  if (programmeId) {
    const programmeRoles = user.programme_roles || [];
    if (programmeRoles.some((r) => r.programme_id === programmeId && ['Editor', 'Viewer'].includes(r.role))) {
      return true;
    }
  }

  // Default: members can view their own tenant's projects (FR-025b).
  //
  // This used to mean "any authenticated user can view any project in the system", which is
  // what stopped the platform hosting more than one client. It now means "any member of this
  // tenant can view this tenant's projects" — the ring fence is the tenant boundary, and the
  // caller only ever receives their own tenant's project ids to pass in here.
  return true;
};

export const canEditProgramme = (user: User | null, programmeId: string): boolean => {
  if (!user) return false;
  if (hasNoActiveTenant(user)) return false;
  if (isPortfolioManager(user)) return true;

  const programmeRoles = user.programme_roles || [];
  return programmeRoles.some((r) => r.programme_id === programmeId && r.role === 'Editor');
};

export const canViewProgramme = (user: User | null, programmeId: string): boolean => {
  if (!user) return false;
  if (hasNoActiveTenant(user)) return false;
  if (isPortfolioManager(user)) return true;

  const programmeRoles = user.programme_roles || [];
  return programmeRoles.some((r) => r.programme_id === programmeId && ['Editor', 'Viewer'].includes(r.role));
};
